import type { PaginationTableColumn } from '@/components/tables/pagination-table/types';
import { toProjectDisplayName } from '@/utils/projects/toProjectDisplayName';
import { fmtCurrency } from 'cesg-shared';
import { useI18n } from 'vue-i18n';
import type { SelectableInvoiceTableInvoice } from './types';

export const useSelectableInvoiceTableColumns =
  (): PaginationTableColumn<SelectableInvoiceTableInvoice>[] => {
    const { t } = useI18n();

    function fmtStatus(status: string): string {
      const statusMap: Record<string, string> = {
        incoming: t('invoiceStatus.incoming'),
        undercertificate: t('invoiceStatus.undercertificate'),
        paid: t('invoiceStatus.paid'),
        parked: t('invoiceStatus.parked'),
        disputed: t('invoiceStatus.disputed'),
        voided: t('invoiceStatus.voided'),
        approved: t('invoiceStatus.approved'),
        'verified-debt': t('invoiceStatus.notPaid'),
      };
      return statusMap[status] ?? status;
    }

    return [
      {
        key: 'serialNumber',
        label: t('adminSupplierInvoice.tableHeaders.serialNumber'),
        sortable: true,
        render: (row) => row.serialNumber,
        compareValue: (row) => Number(row.serialNumber),
        columnWidth: '70px',
      },
      {
        key: 'project',
        label: t('adminSupplierInvoice.tableHeaders.project'),
        sortable: true,
        compareValue: (row) => toProjectDisplayName(row.project),
        render: (row) => toProjectDisplayName(row.project),
        columnWidth: '190px',
      },
      {
        key: 'accounts',
        label: t('adminSupplierInvoice.tableHeaders.accountType'),
        sortable: true,
        render: (row) =>
          row.accounts.map((a) => `${a.number} - ${a.name}`).join(', '),
        columnWidth: '200px',
      },
      {
        key: 'invoiceNumber',
        label: t('adminSupplierInvoice.tableHeaders.invoiceNumber'),
        sortable: true,
        columnWidth: '90px',
        render: (row) => row.invoiceNumber,
      },
      {
        key: 'status',
        label: t('adminSupplierInvoice.tableHeaders.status'),
        sortable: true,
        render: (row) => fmtStatus(row.status),
        columnWidth: '100px',
      },
      {
        key: 'supplier',
        label: t('adminSupplierInvoice.tableHeaders.supplier'),
        sortable: true,
        render: (row) => row.supplier,
        columnWidth: '180px',
      },
      {
        key: 'totalAmount',
        label: t('adminSupplierInvoice.tableHeaders.totalAmount'),
        sortable: true,
        render: (row) => fmtCurrency(row.totalAmount),
        compareValue: (row) => row.totalAmount,
        columnWidth: '120px',
      },
      {
        key: 'vat',
        label: t('adminSupplierInvoice.tableHeaders.vat'),
        sortable: true,
        render: (row) => fmtCurrency(row.vat),
        compareValue: (row) => row.vat,
        columnWidth: '120px',
      },
      {
        key: 'invoiceDate',
        label: t('adminSupplierInvoice.tableHeaders.invoiceDate'),
        sortable: true,
        render: (row) => row.invoiceDate,
        compareValue: (row) => new Date(row.invoiceDate).getTime(),
        columnWidth: '120px',
      },
      {
        key: 'dueDate',
        label: t('adminSupplierInvoice.tableHeaders.dueDate'),
        sortable: true,
        render: (row) => row.dueDate,
        compareValue: (row) => new Date(row.dueDate).getTime(),
        columnWidth: '120px',
      },
      {
        key: 'paymentFileId',
        label: t('adminSupplierInvoice.tableHeaders.paymentFileId'),
        sortable: true,
        render: (row) => String(row.paymentFileId ?? '-'),
        compareValue: (row) => row.paymentFileId ?? -Infinity,
        columnWidth: '100px',
      },
    ];
  };
